import { z } from "zod";

import { AddressSchema } from "../address";

/**
 * What a list, a detail page or an export shows for an on-chain crypto payment, which has no payment
 * instrument and no processor: the coin, the network it was quoted on (`Ethereum Sepolia`, `Base`),
 * and whether that network is a testnet (sandbox mode). Either name is null when the mirror row lacks it.
 */
export const PaymentCryptoOnchainSchema = z.object({
    asset: z.string().nullable(),
    network: z.string().nullable(),
    isTest: z.boolean(),
});

export type PaymentCryptoOnchain = z.infer<typeof PaymentCryptoOnchainSchema>;

export const PaymentSchema = z.object({
    id: z.string(),
    amount: z.number(),
    currency: z.string(),
    status: z.string(),
    subStatus: z.string().nullish(),
    createdAt: z.date().default(new Date()),
    updatedAt: z.date().optional(),
    draft: z.boolean().default(false),
    initiatedBy: z.enum(['customer', 'merchant']).nullable().optional(),
    chainIndex: z.number().nullable().optional(),
    retryIndex: z.number().nullable().optional(),
    isRefundable: z.boolean().default(false),
    customer: z.preprocess(
        (val) => {
            if (!val || val === null) return { email: '-', fullName: '-', firstName: '-', lastName: '-' };
            const firstName = (val as any).firstName ?? '';
            const lastName = (val as any).lastName ?? '';
            const fullName = firstName && lastName
                ? `${firstName} ${lastName}`.trim()
                : firstName || lastName || '-';
            return {
                email: (val as any).email ?? '-',
                fullName,
                firstName: firstName || '-',
                lastName: lastName || '-',
            };
        },
        z.object({
            email: z.string(),
            fullName: z.string(),
            firstName: z.string(),
            lastName: z.string(),
        })
    ),
    paymentInstrument: z.preprocess(
        (val) => {
            if (!val || val === null) return { card: null, type: null };
            const v = val as any;
            const card = v.card;
            return {
                type: v.type ?? null,
                card: !card || card === null ? null : {
                    brand: card.brand ?? '-',
                    last4: card.last4 ?? '-',
                    bin: card.bin ?? null,
                },
            };
        },
        z.object({
            type: z.string().nullable().optional(),
            card: z.object({
                brand: z.string(),
                last4: z.string(),
                bin: z.string().nullable().optional(),
            }).nullable(),
        })
    ),
    processorName: z.string().nullable().optional(),
    processor: z.object({
        name: z.string(),
    }).nullable().optional(),
    store: z.object({
        id: z.string(),
        name: z.string(),
    }).nullable().optional(),
    metadata: z.record(z.any()).nullable().optional(),
    /** Set by the list reads on an on-chain crypto payment only; absent everywhere else. */
    cryptoOnchain: PaymentCryptoOnchainSchema.nullable().optional(),
})

const AvsResponseSchema = z.object({
    code: z.string().default('-').optional(),
    message: z.string().default('-').optional(),
    address_check_pass: z.boolean().optional(),
    postal_code_check_pass: z.boolean().optional(),
});

const CvvResponseSchema = z.object({
    code: z.string().default('-').optional(),
    message: z.string().default('-').optional(),
    cvv_check_pass: z.boolean().optional(),
});

const ThreedsSessionSchema = z.object({
    status: z.string().nullable(),
    type: z.string().nullable(),
    cardBrand: z.string().nullable(),
    authenticationType: z.string().nullable(),
    transStatus: z.string().nullable(),
    transStatusLabel: z.string().nullable(),
    messageVersion: z.string().nullable(),
    eci: z.string().nullable(),
}).nullable().optional();

const ErrorSchema = z.object({
    code: z.string().optional(),
    message: z.string().optional(),
    processorCode: z.string().optional(),
    processorMessage: z.string().optional(),
}).nullable().optional();

const ProcessorSchema = z.object({
    name: z.string(),
    id: z.string(),
}).nullable().optional();

const TransactionSchema = z.object({
    id: z.string(),
    type: z.string(),
    status: z.string(),
    amount: z.number(),
    currency: z.string(),
    result: z.string(),
    createdAt: z.date(),
    updatedAt: z.date().nullable().optional(),
    externalTransactionId: z.string().nullable().default('-'),
    avsResponse: AvsResponseSchema.nullable(),
    cvvResponse: CvvResponseSchema.nullable(),
    authorizationCode: z.string().nullable().default('-'),
    threedsSession: ThreedsSessionSchema.nullable(),
    processor: ProcessorSchema,
    processorId: z.string().nullable().optional(),
    accountId: z.string().nullable().optional(),
    paymentId: z.string().nullable().optional(),
    storeId: z.string().nullable().optional(),
    storefrontTransactionId: z.string().nullable().optional(),
    paymentInstrumentId: z.string().nullable().optional(),
    usedProcessorTokenId: z.string().nullable().optional(),
    networkTransactionId: z.string().nullable().optional(),
    isTest: z.boolean().nullable().optional(),
    error: ErrorSchema,
    rawRequest: z.any().nullable().optional(),
    rawResponse: z.any().nullable().optional(),
    initiatedBy: z.string().nullable().optional(),
    reasonType: z.string().nullable().optional(),
    draft: z.boolean().nullable().optional(),
    metadata: z.any().nullable().optional(),
    requireAction: z.any().nullable().optional(),
});

export const PaymentDetailsSchema = z.object({
    id: z.string(),
    amount: z.number(),
    refundedAmount: z.number(),
    currency: z.string(),
    status: z.enum(['pending', 'succeeded', 'error', 'refunded', 'declined', 'expired', 'rejected', 'on_hold']),
    subStatus: z.enum(['authorized', 'waiting_action', 'approved', 'declined', 'captured', 'partially_captured', 'partially_refunded', 'pending_provider_confirm', 'refunded', 'canceled', 'received', 'pending_review', 'error', 'timeout', 'expired', 'rejected', 'unknown']).nullish(),
    orderId: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    draft: z.boolean().default(false),
    initiatedBy: z.enum(['customer', 'merchant']).optional(),
    reasonType: z.enum(['recurring', 'unscheduled', 'installment']).nullable().optional(),
    chainIndex: z.number().optional(),
    retryIndex: z.number().optional(),
    metadata: z.any().optional(),
    requireAction: z.enum(['none', 'redirect', 'error', '3ds-challenge', 'radar', 'stripe_express_checkout']).optional(),
    requireActionData: z.any().optional(),
    error: z.any().optional(),
    customer: z.object({
        id: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
        billingAddress: AddressSchema.nullable().optional(),
        shippingAddress: AddressSchema.nullable().optional(),
    }),
    paymentInstrument: z.object({
        id: z.string(),
        type: z.string(),
        token: z.string().nullable(),
        tokenizer: z.string().nullable(),
        metadata: z.any().nullable(),
        card: z.object({
            last4: z.string().optional(),
            exp_month: z.number().optional(),
            exp_year: z.number().optional(),
            brand: z.string().optional(),
            type: z.string().optional(),
            issuer: z.string().optional(),
            country: z.string().optional(),
            bin: z.string().optional(),
        }).nullable(),
        cardEnrichment: z.object({
            id: z.string(),
            data: z.any(),
        }).nullable(),
        processorTokens: z.array(z.object({
            id: z.string(),
            token: z.string(),
            processor: z.object({
                id: z.string(),
                name: z.string(),
                type: z.string(),
            }).nullable(),
        })).nullable(),
    }).nullable(),
    transactions: z.array(TransactionSchema),
    order: z.object({
        id: z.string(),
        status: z.string(),
        paidAt: z.date().optional().nullable(),
        paidAmount: z.number().nullable(),
        currency: z.string().nullable(),
        externalOrderId: z.string().nullable().default('-'),
    }).nullable(),
    // Store information
    storeId: z.string().nullable().optional(),
    store: z.object({
        id: z.string(),
        name: z.string(),
    }).nullable().optional(),
    // Payment Flow used for this payment
    paymentFlowId: z.string().nullable().optional(),
    paymentFlow: z.object({
        id: z.string(),
        name: z.string(),
    }).nullable().optional(),
    // Subscription linked to this payment's order
    subscriptionId: z.string().nullable().optional(),
    // Dispute state, from the payfac dispute sync (dispute_case_id) or a chargeback alert. TAG-2899.
    isDisputed: z.boolean().nullish(),
    disputeStatus: z.string().nullish(),
    disputeType: z.string().nullish(),
    disputeReasonCode: z.string().nullish(),
    disputeCaseId: z.string().nullish(),
    disputedAt: z.date().nullish(),
    disputeNote: z.string().nullish(),
    /** Set on an on-chain crypto payment only (no instrument, no processor): its coin, network and testnet flag. */
    cryptoOnchain: PaymentCryptoOnchainSchema.nullable().optional(),
});

export type Payment = z.infer<typeof PaymentSchema>;
export type PaymentDetails = z.infer<typeof PaymentDetailsSchema>;
export type Transaction = z.infer<typeof TransactionSchema>;
export const PaymentDto = (object: unknown): Payment => PaymentSchema.parse(object);
export const PaymentDetailsDto = (object: unknown): PaymentDetails => PaymentDetailsSchema.parse(object);